import { createContext, useContext, type ReactNode } from 'react';

/** Args passed to the injected speaker-label renderer for one transcript turn. */
export interface SpeakerLabelRendererArgs {
  recordingId: string;
  speakerId: number;
  calendarEventId: string | null;
  /** Entity the attendees live on (the recording's notes entity the reader uses). */
  entityId: string | null;
  /** This recording's audio surface — scopes the attendee roster. */
  surfaceId: string | null;
}

export type SpeakerLabelRenderer = (args: SpeakerLabelRendererArgs) => ReactNode;

const SpeakerLabelContext = createContext<SpeakerLabelRenderer | null>(null);

/**
 * Injects an editable speaker-label renderer into the transcript reader.
 *
 * The concrete editor (features/recordings/SpeakerLabelEdit) couples to
 * feature-internal hooks, so the lib-layer reader can't import it directly
 * (Bulletproof). The app layer mounts the provider with the real renderer;
 * TranscriptTurn consumes it via useSpeakerLabelRenderer and falls back to a
 * plain name when no provider is present.
 */
export function SpeakerLabelProvider({
  renderer,
  children,
}: {
  renderer: SpeakerLabelRenderer;
  children: ReactNode;
}) {
  return (
    <SpeakerLabelContext.Provider value={renderer}>{children}</SpeakerLabelContext.Provider>
  );
}

export function useSpeakerLabelRenderer(): SpeakerLabelRenderer | null {
  return useContext(SpeakerLabelContext);
}
